import type { Entrepreneur, EventRecord } from '@/api/entrepreneurs';
import { Button } from '@/components/Button';
import { Card, CardHead } from '@/components/Card';
import { DeclareEventModal } from '@/components/DeclareEventModal';
import { EmptyState } from '@/components/EmptyState';
import { EventDrawer } from '@/components/EventDrawer';
import type { IconName } from '@/components/Icon';
import { Icon } from '@/components/Icon';
import { ResolveConflictModal } from '@/components/ResolveConflictModal';
import { Stat } from '@/components/Stat';
import { Status } from '@/components/Status';
import { Timeline, TimelineItem } from '@/components/Timeline';
import { useEntrepreneur, useEntrepreneurTimeline } from '@/hooks/useEntrepreneurs';
import {
  attrTypeIcon,
  attrTypeLabel,
  fmt,
  formatDateTime,
  formatPhone,
  initials,
  sourceLabel,
} from '@/lib/format';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';

type Tab = 'resume' | 'timeline' | 'documents' | 'conflits';

/**
 * Fiche entreprenant 360° — port of MAFA's `Fiche` component.
 *
 * The page has four tabs:
 *   • Résumé        — three KPI stats + identity + references
 *   • Timeline      — chronological event journal with payload drawer
 *   • Documents     — empty-state placeholder (real impl in sprint 6 slice 3)
 *   • Conflits      — only visible when reconciliationStatus = CONFLIT
 */
export function DossierPage() {
  const { phone } = useParams({ from: '/_auth/dossiers/$phone' });
  const navigate = useNavigate();
  const entQuery = useEntrepreneur(phone);
  const [tab, setTab] = useState<Tab>('resume');
  const [declareOpen, setDeclareOpen] = useState(false);

  if (entQuery.isLoading) {
    return (
      <div className="fade-in" style={{ padding: 22, color: 'var(--ink-3)', fontWeight: 600 }}>
        Chargement de la fiche…
      </div>
    );
  }

  const ent = entQuery.data;
  if (!ent) {
    return (
      <div className="fade-in">
        <BackLink />
        <Card pad>
          <EmptyState
            icon="search"
            title="Aucun dossier"
            subtitle={`Aucun entreprenant n'est connu pour ${phone}.`}
          />
        </Card>
      </div>
    );
  }

  const tabs: Array<{ key: Tab; label: string; icon: IconName; count?: number }> = [
    { key: 'resume', label: 'Résumé', icon: 'user' },
    { key: 'timeline', label: 'Timeline', icon: 'activity', count: Number(ent.eventCount) },
    { key: 'documents', label: 'Documents', icon: 'doc' },
  ];
  if (ent.reconciliationStatus === 'CONFLIT') {
    tabs.push({ key: 'conflits', label: 'Conflits', icon: 'alert', count: 1 });
  }

  return (
    <div className="fade-in">
      <BackLink onClick={() => navigate({ to: '/dossiers' })} />
      <FicheHeader ent={ent} onDeclare={() => setDeclareOpen(true)} />

      <DeclareEventModal
        opened={declareOpen}
        onClose={() => setDeclareOpen(false)}
        entrepreneurPhone={ent.phoneNumber}
        entrepreneurName={displayName(ent)}
      />

      <div className="tabs">
        {tabs.map(t => (
          <button key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>
            <Icon name={t.icon} />
            {t.label}
            {t.count != null && <span className="n">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'resume' && <FicheResume ent={ent} />}
      {tab === 'timeline' && <FicheTimelineTab phone={phone} />}
      {tab === 'documents' && (
        <EmptyState
          icon="doc"
          title="Aucun document rattaché"
          subtitle="Le rattachement de pièces et photos arrivera dans un sprint ultérieur."
        />
      )}
      {tab === 'conflits' && <FicheConflits ent={ent} />}
    </div>
  );
}

function BackLink({ onClick }: { onClick?: () => void }) {
  if (onClick) {
    return (
      <Button
        variant="ghost"
        size="sm"
        icon="arrowleft"
        onClick={onClick}
        style={{ marginBottom: 16 }}
      >
        Retour aux dossiers
      </Button>
    );
  }
  return (
    <Link
      to="/dossiers"
      className="btn ghost sm"
      style={{ marginBottom: 16, display: 'inline-flex' }}
    >
      <Icon name="arrowleft" />
      Retour aux dossiers
    </Link>
  );
}

function FicheHeader({ ent, onDeclare }: { ent: Entrepreneur; onDeclare: () => void }) {
  const name = displayName(ent);
  return (
    <Card pad style={{ marginBottom: 22 }}>
      <div className="fiche-head" style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
        <div
          style={{
            width: 62,
            height: 62,
            borderRadius: 16,
            background: 'var(--accent-tint-2)',
            color: 'var(--accent-ink)',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 800,
            fontSize: 23,
            flex: '0 0 auto',
          }}
        >
          {initials(name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-.025em' }}>
              {name}
            </h2>
            <Status kind={ent.reconciliationStatus === 'CONFLIT' ? 'warn' : 'ok'}>
              {ent.reconciliationStatus === 'CONFLIT' ? 'En conflit' : 'Réconcilié'}
            </Status>
            <span className="meta">{ent.enrichmentStatus}</span>
          </div>
          {(ent.businessName || ent.primaryActivity) && (
            <div style={{ color: 'var(--ink-2)', fontWeight: 600, fontSize: 14.5, marginTop: 4 }}>
              {[ent.businessName, ent.primaryActivity].filter(Boolean).join(' · ')}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              marginTop: 12,
              color: 'var(--ink-3)',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}>
              <Icon name="phone" size={15} />
              <span style={{ fontFamily: 'var(--mono)' }}>{formatPhone(ent.phoneNumber)}</span>
            </span>
            {ent.commune && (
              <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}>
                <Icon name="pin" size={15} />
                {ent.commune}
              </span>
            )}
            {ent.lastEventAt && (
              <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}>
                <Icon name="clock" size={15} />
                Dernier événement {formatDateTime(ent.lastEventAt)}
              </span>
            )}
          </div>
        </div>
        <div className="fiche-actions" style={{ display: 'flex', gap: 10, flex: '0 0 auto' }}>
          <Button icon="export">Exporter</Button>
          <Button variant="primary" icon="plus" onClick={onDeclare}>
            Déclarer un événement
          </Button>
        </div>
      </div>
    </Card>
  );
}

function FicheResume({ ent }: { ent: Entrepreneur }) {
  return (
    <div className="grid grid-3 fade-in">
      <Stat
        label="Événements connus"
        value={fmt(ent.eventCount)}
        delta="depuis l'enregistrement du dossier"
        icon="activity"
        tint
      />
      <Stat
        label="Dernière activité"
        value={ent.lastEventAt ? formatDateTime(ent.lastEventAt).split(' ')[0] : '—'}
        delta={ent.lastEventAt ? `à ${formatDateTime(ent.lastEventAt).split(' ')[1] ?? ''}` : ''}
        icon="clock"
      />
      <Stat
        label="Enrichissement"
        value={ent.enrichmentStatus}
        delta={ent.reconciliationStatus === 'CONFLIT' ? 'Conflit à arbitrer' : 'Réconciliation OK'}
        trend={ent.reconciliationStatus === 'CONFLIT' ? 'down' : 'up'}
        icon="shieldcheck"
      />

      <div className="card" style={{ gridColumn: 'span 2' }}>
        <CardHead title="Identité consolidée" subtitle="projection master data" />
        <div style={{ padding: '6px 22px 14px' }}>
          <dl className="kv">
            <dt>Nom affiché</dt>
            <dd>{ent.displayName ?? '—'}</dd>
            <dt>Nom</dt>
            <dd>{[ent.firstname, ent.lastname].filter(Boolean).join(' ') || '—'}</dd>
            <dt>Nom de commerce</dt>
            <dd>{ent.businessName ?? '—'}</dd>
            <dt>Activité principale</dt>
            <dd>{ent.primaryActivity ?? '—'}</dd>
            <dt>Secteur</dt>
            <dd>{ent.sector ?? '—'}</dd>
            <dt>Compte Orange Money</dt>
            <dd style={{ fontFamily: 'var(--mono)' }}>
              {ent.omAccountPhone
                ? `${formatPhone(ent.omAccountPhone)}${
                    ent.omAccountCreatedAt
                      ? ` · ${new Date(ent.omAccountCreatedAt).toLocaleDateString('fr-FR')}`
                      : ''
                  }`
                : '—'}
            </dd>
            <dt>Téléphone</dt>
            <dd style={{ fontFamily: 'var(--mono)' }}>{formatPhone(ent.phoneNumber)}</dd>
            {ent.aliases && ent.aliases.length > 0 && (
              <>
                <dt>Autres numéros</dt>
                <dd style={{ fontFamily: 'var(--mono)' }}>{ent.aliases.map(formatPhone).join(' · ')}</dd>
              </>
            )}
            <dt>Commune</dt>
            <dd>{ent.commune ?? '—'}</dd>
            <dt>Coordonnées GPS</dt>
            <dd style={{ fontFamily: 'var(--mono)' }}>
              {ent.gpsLatitude != null && ent.gpsLongitude != null
                ? `${ent.gpsLatitude.toFixed(5)}, ${ent.gpsLongitude.toFixed(5)}`
                : '—'}
            </dd>
            <dt>Créé le</dt>
            <dd style={{ fontFamily: 'var(--mono)' }}>{formatDateTime(ent.createdAt)}</dd>
            <dt>Mis à jour</dt>
            <dd style={{ fontFamily: 'var(--mono)' }}>{formatDateTime(ent.updatedAt)}</dd>
          </dl>
        </div>
      </div>

      <div className="card">
        <CardHead title="Références externes" />
        <div style={{ padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Object.entries(ent.externalRefs ?? {}).length === 0 && (
            <span className="muted" style={{ fontSize: 13, fontWeight: 500 }}>
              Aucune référence externe enregistrée.
            </span>
          )}
          {Object.entries(ent.externalRefs ?? {}).map(([k, v]) => (
            <div
              key={k}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <span className="meta">
                <span className="sq" />
                {k.toUpperCase()}
              </span>
              <span className="ref">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FicheTimelineTab({ phone }: { phone: string }) {
  const [type, setType] = useState('');
  const [source, setSource] = useState('');
  const [selected, setSelected] = useState<EventRecord | null>(null);
  const query = useEntrepreneurTimeline(phone, { type: type || undefined, limit: 200 });

  const events = (query.data ?? []).filter(e => !source || e.sourceSystem === source);
  const allSources = uniq((query.data ?? []).map(e => e.sourceSystem));
  const allTypes = uniq((query.data ?? []).map(e => e.attributeType));

  return (
    <div className="fade-in">
      <div className="toolbar">
        <label className="input">
          <Icon name="filter" />
          <select value={type} onChange={e => setType(e.target.value)}>
            <option value="">Tous les types</option>
            {allTypes.map(t => (
              <option key={t} value={t}>
                {attrTypeLabel(t)}
              </option>
            ))}
          </select>
        </label>
        <label className="input">
          <Icon name="layers" />
          <select value={source} onChange={e => setSource(e.target.value)}>
            <option value="">Toutes les sources</option>
            {allSources.map(s => (
              <option key={s} value={s}>
                {sourceLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <div className="muted" style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 600 }}>
          {events.length} événement{events.length > 1 ? 's' : ''}, du plus récent au plus ancien
        </div>
      </div>

      {query.isLoading && (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', fontWeight: 600 }}>
          Chargement…
        </div>
      )}
      {!query.isLoading && events.length === 0 && (
        <EmptyState
          icon="activity"
          title="Aucun événement"
          subtitle="Aucun événement ne correspond à ces filtres."
        />
      )}
      <Timeline>
        {events.map((e, i) => (
          <TimelineItem
            key={e.eventId}
            icon={attrTypeIcon(e.attributeType) as IconName}
            title={attrTypeLabel(e.attributeType)}
            when={formatDateTime(e.occurredAt)}
            meta={
              <span className="meta">
                <span className="sq" />
                {sourceLabel(e.sourceSystem)}
              </span>
            }
            footer={
              <>
                {e.sourceActorId && (
                  <span className="meta">
                    <Icon name="user" size={13} />
                    {e.sourceActorId}
                  </span>
                )}
                <span className="meta">schéma v{e.schemaVersion}</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    color: 'var(--accent-2)',
                    fontWeight: 700,
                    fontSize: 12,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  Voir le détail
                  <Icon name="chevright" size={13} />
                </span>
              </>
            }
            showLine={i < events.length - 1}
            onClick={() => setSelected(e)}
          />
        ))}
      </Timeline>

      <EventDrawer event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function FicheConflits({ ent }: { ent: Entrepreneur }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="fade-in">
      <Card style={{ borderColor: 'var(--alert-tint-2)' }}>
        <div
          className="card-head"
          style={{ background: 'var(--alert-tint)', borderColor: 'var(--alert-tint-2)' }}
        >
          <div
            className="tl-node"
            style={{ color: 'var(--alert-ink)', borderColor: 'var(--alert-tint-2)' }}
          >
            <Icon name="alert" />
          </div>
          <div>
            <div className="ct">Réconciliation à arbitrer</div>
            <div className="cs">
              {ent.phoneNumber} est marqué CONFLIT — un résolveur d'identité a détecté une
              collision.
            </div>
          </div>
        </div>
        <div style={{ padding: 22 }}>
          <p style={{ fontSize: 13.5, color: 'var(--ink-2)', marginTop: 0, lineHeight: 1.55 }}>
            Choisissez une décision : <strong>fusionner</strong> rattache les événements à un
            téléphone canonique unique ; <strong>garder séparés</strong> confirme qu'il s'agit bien
            d'entreprenants distincts. Un événement <span className="code">IDENTITY_MERGE</span> est
            journalisé et la marque CONFLIT est retirée.
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <Button variant="primary" icon="link" onClick={() => setOpen(true)}>
              Arbitrer le conflit
            </Button>
          </div>
        </div>
      </Card>

      <ResolveConflictModal
        opened={open}
        onClose={() => setOpen(false)}
        phone={ent.phoneNumber}
        entrepreneurName={displayName(ent)}
      />
    </div>
  );
}

function displayName(e: Entrepreneur): string {
  return (
    e.displayName ||
    e.businessName ||
    [e.firstname, e.lastname].filter(Boolean).join(' ') ||
    e.phoneNumber
  );
}

function uniq<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}
